import React, { useState, useEffect } from 'react';
import { X, Download, Database, Filter, CheckCircle, ChevronRight, ChevronLeft } from 'lucide-react';
import { exportLeads } from '../services/exportService';
import { showToast } from '@/components/ui/Toast';
import ColumnsService from '@/utils/columnsCache';
import { optimizedApi } from '@/utils/api/optimized-api';
import { LeadProfileSettings, PROFILE_FIELD_LABELS } from '@/types/leads/Profile';

interface ExportModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedRows: string[];
    totalFilteredCount?: number;
    currentFilters?: any;
    apiEndpoint?: string; // Custom API endpoint for export (default: '/leads')
}

interface FieldOption {
    id: string;
    label: string;
    type: 'primary' | 'custom' | 'tracking' | 'profile';
    category?: string;
}

const ExportModalWithFieldSelection: React.FC<ExportModalProps> = ({
    isOpen,
    onClose,
    selectedRows,
    totalFilteredCount = 0,
    currentFilters = {},
    apiEndpoint = '/leads'
}) => {
    const [step, setStep] = useState<1 | 2>(1);
    const [exportType, setExportType] = useState<'all' | 'filtered' | 'selected'>(
        selectedRows.length > 0 ? 'selected' : 'filtered'
    );
    const [exportFormat, setExportFormat] = useState<'csv' | 'excel'>('csv');
    const [includeUTMData, setIncludeUTMData] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
    const [exportComplete, setExportComplete] = useState(false);

    // Field selection state
    const [availableFields, setAvailableFields] = useState<FieldOption[]>([]);
    const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set());
    const [selectAllPrimary, setSelectAllPrimary] = useState(true);
    const [selectAllCustom, setSelectAllCustom] = useState(true);
    const [selectAllTracking, setSelectAllTracking] = useState(false);
    const [selectAllProfile, setSelectAllProfile] = useState(false);
    // Vista Form clients see primary, custom and Vista Form fields as one "Form Fields" section.
    const [mergeFormFields, setMergeFormFields] = useState(false);

    // Load available fields and reset export type when modal opens
    useEffect(() => {
        if (isOpen) {
            loadAvailableFields();
            // Reset export type based on current selection
            setExportType(selectedRows.length > 0 ? 'selected' : 'filtered');
        }
    }, [isOpen, selectedRows.length]);

    const loadAvailableFields = async () => {
        try {
            const columnsResponse = await ColumnsService.getColumns();
            const columns = columnsResponse.columns || [];
            const fields: FieldOption[] = [];

            // Check if this is an admin export
            const isAdminExport = apiEndpoint.includes('/leads/admin');

            // Primary fields (standard fields)
            // For admin exports, exclude regular source/status as they use admin tracking versions
            const primaryFields = [
                { id: 'phone', label: 'Phone', type: 'primary' as const },
                { id: 'name', label: 'Name', type: 'primary' as const },
                { id: 'email', label: 'Email', type: 'primary' as const },
                ...(!isAdminExport ? [{ id: 'status', label: 'Status', type: 'primary' as const }] : []),
                ...(!isAdminExport ? [{ id: 'source', label: 'Source', type: 'primary' as const }] : []),
                { id: 'grade', label: 'Grade', type: 'primary' as const },
                { id: 'created_date', label: 'Created Date', type: 'primary' as const },
                { id: 'last_updated_date', label: 'Last Updated Date', type: 'primary' as const },
                ...(!isAdminExport ? [
                    // Exported as one column: date with response time, e.g. "2026-10-29 16:01:17 (27m)"
                    { id: 'first_updated_date', label: 'First Updated Date', type: 'primary' as const },
                ] : []),
                { id: 'message', label: 'Message', type: 'primary' as const },
                { id: 'owner', label: 'Owner', type: 'primary' as const },
                { id: 'client_remarks', label: 'Client History', type: 'primary' as const },
                ...(isAdminExport ? [{ id: 'admin_remarks', label: 'Admin History', type: 'primary' as const }] : []),
                { id: 'client_last_comment', label: 'Client Last Comment', type: 'primary' as const },
                ...(isAdminExport ? [{ id: 'admin_last_comment', label: 'Admin Last Comment', type: 'primary' as const }] : []),
                { id: 'first_remark_date', label: 'First Remark Date', type: 'primary' as const },
            ];

            // Admin-specific fields (only show for admin exports)
            if (isAdminExport) {
                primaryFields.push(
                    { id: 'school_name', label: 'School Name', type: 'primary' as const },
                    { id: 'school_area', label: 'School Area', type: 'primary' as const },
                    { id: 'school_city', label: 'School City', type: 'primary' as const },
                    { id: 'admin_source', label: 'Source', type: 'primary' as const },
                    { id: 'sub_source', label: 'Sub Source', type: 'primary' as const },
                    { id: 'client_status', label: 'Client Status', type: 'primary' as const },
                    { id: 'admin_status', label: 'Admin Status', type: 'primary' as const },
                    { id: 'admin_sub_status', label: 'Sub Status', type: 'primary' as const },
                    { id: 'admin_alternative_phone', label: 'Alternative Phone', type: 'primary' as const },
                    { id: 'admin_child_name', label: 'Child Name', type: 'primary' as const },
                    { id: 'admin_suggest_area', label: 'Suggest Area', type: 'primary' as const },
                    { id: 'admin_suggest_city', label: 'Suggest City', type: 'primary' as const },
                    { id: 'admin_prospect', label: 'Prospect', type: 'primary' as const },
                    { id: 'admin_near_by_client', label: 'Near By Client', type: 'primary' as const },
                    { id: 'admin_curriculum', label: 'Curriculum', type: 'primary' as const },
                    { id: 'admin_category', label: 'Category', type: 'primary' as const },
                    { id: 'admin_admission_planning', label: 'Admission Planning', type: 'primary' as const },
                    { id: 'admin_budget', label: 'Budget', type: 'primary' as const },
                    { id: 'admin_form_type', label: 'Form Type', type: 'primary' as const },
                    { id: 'admin_message', label: 'Admin Message', type: 'primary' as const },
                    { id: 'admin_is_qualified', label: 'Is Qualified', type: 'primary' as const },
                    { id: 'firstAdminUpdate', label: 'First Admin Updated Date', type: 'primary' as const },
                );
            }

            fields.push(...primaryFields);

            console.log('📋 Loaded columns for field selection:', columns.length, 'columns');
            if (columns.length > 0) {
                console.log('📋 First 3 columns:', columns.slice(0, 3));
            }

            // Custom fields from columns - SKIP for admin exports (admin uses admin tracking fields only)
            if (!isAdminExport && columns && columns.length > 0) {
                console.log('📋 All columns received from API:', columns.length);
                console.log('📋 All column details:', columns.map(c => ({
                    name: c.col_name,
                    id: c.col_id,
                    label: c.col_label,
                    uuid: c.uuid,
                    fullObject: c
                })));

                const customFields = columns
                    .filter(col => {
                        // Filter out standard fields (including grade which is a primary field)
                        const isStandardField = ['phone', 'name', 'email', 'status', 'source', 'grade', 'created_date', 'last_updated_date', 'message'].includes(col.col_name);

                        // Check for col_id or uuid - explicitly check for null/undefined, not falsy (0 is valid!)
                        const hasColId = col.col_id !== null && col.col_id !== undefined;
                        const hasUuid = col.uuid !== null && col.uuid !== undefined;
                        const hasIdentifier = hasColId || hasUuid;

                        console.log(`📋 Column: ${col.col_name}, isStandard: ${isStandardField}, hasColId: ${hasColId}, col_id: ${col.col_id}, hasUuid: ${hasUuid}, uuid: ${col.uuid}`);

                        if (!hasIdentifier && !isStandardField) {
                            console.warn('⚠️ Skipping NON-STANDARD column without col_id or uuid:', col.col_name, col);
                        }

                        return !isStandardField && hasIdentifier;
                    })
                    .map(col => {
                        // Use col_id if available, otherwise use uuid
                        const identifier = col.col_id !== null && col.col_id !== undefined ? col.col_id : col.uuid;
                        return {
                            id: `custom_${identifier}`,
                            label: col.col_label || col.col_name,
                            type: 'custom' as const
                        };
                    });

                console.log('📋 Custom fields after filtering:', customFields.length);
                console.log('📋 Custom fields:', customFields);
                console.log('📋 Standard fields that were filtered out:',
                    columns.filter(c => ['phone', 'name', 'email', 'status', 'source', 'grade', 'created_date', 'last_updated_date', 'message'].includes(c.col_name)).length
                );
                console.log('📋 Fields without col_id or uuid that were skipped:',
                    columns.filter(c => {
                        const isStandardField = ['phone', 'name', 'email', 'status', 'source', 'grade', 'created_date', 'last_updated_date', 'message'].includes(c.col_name);
                        const hasColId = c.col_id !== null && c.col_id !== undefined;
                        const hasUuid = c.uuid !== null && c.uuid !== undefined;
                        const hasIdentifier = hasColId || hasUuid;
                        return !isStandardField && !hasIdentifier;
                    }).map(c => ({ name: c.col_name, label: c.col_label, full: c }))
                );

                fields.push(...customFields);
            } else if (isAdminExport) {
                console.log('📋 Admin export - skipping custom fields (using admin tracking fields only)');
            }

            // Tracking fields (UTM, Facebook, LinkedIn)
            const trackingFields = [
                // UTM Fields
                { id: 'utm_source', label: 'UTM Source', type: 'tracking' as const, category: 'UTM' },
                { id: 'utm_medium', label: 'UTM Medium', type: 'tracking' as const, category: 'UTM' },
                { id: 'utm_campaign', label: 'UTM Campaign', type: 'tracking' as const, category: 'UTM' },
                { id: 'utm_content', label: 'UTM Content', type: 'tracking' as const, category: 'UTM' },
                { id: 'utm_term', label: 'UTM Term', type: 'tracking' as const, category: 'UTM' },
                { id: 'utm_location', label: 'UTM Location', type: 'tracking' as const, category: 'UTM' },
                { id: 'utm_device', label: 'UTM Device', type: 'tracking' as const, category: 'UTM' },
                { id: 'utm_network', label: 'UTM Network', type: 'tracking' as const, category: 'UTM' },
                { id: 'utm_platform', label: 'UTM Platform', type: 'tracking' as const, category: 'UTM' },
                { id: 'utm_campaign_id', label: 'UTM Campaign Id', type: 'tracking' as const, category: 'UTM' },
                { id: 'utm_adposition', label: 'UTM Ad Position', type: 'tracking' as const, category: 'UTM' },
                { id: 'gclid', label: 'Google Click ID', type: 'tracking' as const, category: 'UTM' },
                { id: 'fclid', label: 'Facebook Click ID', type: 'tracking' as const, category: 'UTM' },
                // Facebook Fields
                { id: 'leadgen_id', label: 'Leadgen ID', type: 'tracking' as const, category: 'Facebook' },
                { id: 'form_id', label: 'Form ID', type: 'tracking' as const, category: 'Facebook' },
                { id: 'form_name', label: 'Form Name', type: 'tracking' as const, category: 'Facebook' },
                { id: 'fb_platform', label: 'Platform', type: 'tracking' as const, category: 'Facebook' },
                { id: 'fb_ad_id', label: 'Ad ID', type: 'tracking' as const, category: 'Facebook' },
                { id: 'fb_ad_name', label: 'Ad Name', type: 'tracking' as const, category: 'Facebook' },
                { id: 'fb_adset_id', label: 'Adset ID', type: 'tracking' as const, category: 'Facebook' },
                { id: 'fb_adset_name', label: 'Adset Name', type: 'tracking' as const, category: 'Facebook' },
                { id: 'fb_campaign_id', label: 'Campaign ID', type: 'tracking' as const, category: 'Facebook' },
                { id: 'fb_campaign_name', label: 'Campaign Name', type: 'tracking' as const, category: 'Facebook' },
                { id: 'fb_is_organic', label: 'Is Organic', type: 'tracking' as const, category: 'Facebook' },
                // LinkedIn Fields
                { id: 'li_campaign_name', label: 'Campaign Name', type: 'tracking' as const, category: 'LinkedIn' },
                { id: 'li_campaign_id', label: 'Campaign ID', type: 'tracking' as const, category: 'LinkedIn' },
                { id: 'li_form_name', label: 'Form Name', type: 'tracking' as const, category: 'LinkedIn' },
                { id: 'li_form_id', label: 'Form ID', type: 'tracking' as const, category: 'LinkedIn' },
            ];

            fields.push(...trackingFields);

            // Extended Lead Profile fields — populated only for clients with
            // the extended lead profile feature active (crm_lead_details),
            // e.g. Vista International School. Harmless to always offer
            // these as options; they'll just be empty for leads/clients that
            // don't have profile data.
            // Named and grouped exactly as in the Vista Form (PROFILE_FIELD_LABELS).
            const profileField = (id: string, category: string) => ({ id, label: PROFILE_FIELD_LABELS[id] || id, type: 'profile' as const, category });
            const profileFields = [
                profileField('lead_source_category', 'Lead Source'),
                profileField('lead_source_subcategory', 'Lead Source'),
                ...['academic_year_joining', 'curriculum', 'student_first_name', 'student_last_name', 'date_of_birth', 'gender'].map((id) => profileField(id, 'Student')),
                ...['is_existing_parent', 'father_name', 'father_occupation', 'father_company', 'father_qualification',
                    'mother_name', 'mother_occupation', 'mother_company', 'mother_qualification'].map((id) => profileField(id, 'Guardian')),
                ...['current_city', 'current_country', 'is_local', 'residential_address'].map((id) => profileField(id, 'Residence')),
                profileField('brand_awareness', 'Brand Awareness'),
                ...['is_contactable', 'last_contacted_date', 'next_followup_date'].map((id) => profileField(id, 'Communication History')),
                ...['summary_notes', 'counsellor_qualification', 'disqualified_reason', 'disqualified_reason_notes', 'overall_status',
                    'counsellor_priority', 'closed_dead_reason', 'competitor_school_joined', 'closed_dead_reason_notes',
                    'consideration_1', 'consideration_2', 'stage_of_admission', 'admission_date'].map((id) => profileField(id, 'Qualification & Status')),
            ];

            // Only offer these as export options for clients that actually
            // have the extended lead profile feature active (currently just
            // Vista International School) — same gate LeadDetailsSidebar and
            // EnhancedLeadsTable already use for the Marketing tab / extra
            // columns, so this stays invisible and inert for every other
            // client rather than showing 38 fields that would always be empty.
            try {
                const profileSettingsRes = await optimizedApi.get<LeadProfileSettings>('/lead-profile/settings');
                const settings = profileSettingsRes.success ? profileSettingsRes.data : null;
                const profileFeatureActive = !!settings?.is_active && (settings.sections_enabled?.length || 0) > 0;
                setMergeFormFields(profileFeatureActive);
                if (profileFeatureActive) {
                    // Vista Form clients don't use these native fields (Status/Source are replaced by
                    // Vista Form fields; history/comments/message aren't used), and Owner/Created Date
                    // take the names the Vista Form gives them.
                    const unused = new Set(['status', 'source', 'message', 'client_remarks', 'client_last_comment', 'first_remark_date']);
                    const vistaNames: Record<string, string> = {
                        owner: 'Counsellor Assigned', created_date: 'Lead Created Date',
                        name: 'Contact Name', phone: 'Contact Phone', email: 'Contact Email',
                    };
                    for (let i = fields.length - 1; i >= 0; i--) {
                        if (fields[i].type === 'primary' && unused.has(fields[i].id)) fields.splice(i, 1);
                        else if (fields[i].type === 'primary' && vistaNames[fields[i].id]) fields[i] = { ...fields[i], label: vistaNames[fields[i].id] };
                    }
                    fields.push(...profileFields);
                }
            } catch (profileSettingsError) {
                console.error('Error checking extended profile feature status:', profileSettingsError);
                // Fail closed — don't show the section if we can't confirm it's active
            }

            setAvailableFields(fields);

            // Initially select all primary and custom fields (and, in the merged Form Fields
            // section, the Vista Form fields too)
            const hasProfile = fields.some(f => f.type === 'profile');
            const defaultSelected = new Set(
                fields.filter(f => f.type === 'primary' || f.type === 'custom' || (hasProfile && f.type === 'profile')).map(f => f.id)
            );
            setSelectedFields(defaultSelected);
            if (hasProfile) setSelectAllProfile(true);
        } catch (error) {
            console.error('Error loading fields:', error);
        }
    };

    const toggleField = (fieldId: string) => {
        // Phone field is always required, don't allow deselection
        if (fieldId === 'phone') {
            return;
        }

        const newSelected = new Set(selectedFields);
        if (newSelected.has(fieldId)) {
            newSelected.delete(fieldId);
        } else {
            newSelected.add(fieldId);
        }
        setSelectedFields(newSelected);

        // Update select all states
        updateSelectAllStates(newSelected);
    };

    const toggleSelectAll = (type: 'primary' | 'custom' | 'tracking' | 'profile') => {
        const fieldsOfType = availableFields.filter(f => f.type === type);
        const newSelected = new Set(selectedFields);

        const allSelected = fieldsOfType.every(f => newSelected.has(f.id));

        if (allSelected) {
            // Deselect all of this type (except phone)
            fieldsOfType.forEach(f => {
                if (f.id !== 'phone') {
                    newSelected.delete(f.id);
                }
            });
        } else {
            // Select all of this type
            fieldsOfType.forEach(f => newSelected.add(f.id));
        }

        setSelectedFields(newSelected);
        updateSelectAllStates(newSelected);
    };

    const formSectionTypes = ['primary', 'custom', 'profile'];
    const toggleSelectAllFormFields = () => {
        const formFields = availableFields.filter(f => formSectionTypes.includes(f.type));
        const newSelected = new Set(selectedFields);
        const allSelected = formFields.every(f => newSelected.has(f.id));
        formFields.forEach(f => {
            if (allSelected) { if (f.id !== 'phone') newSelected.delete(f.id); }
            else newSelected.add(f.id);
        });
        setSelectedFields(newSelected);
        updateSelectAllStates(newSelected);
    };

    const updateSelectAllStates = (selected: Set<string>) => {
        const primaryFields = availableFields.filter(f => f.type === 'primary');
        const customFields = availableFields.filter(f => f.type === 'custom');
        const trackingFields = availableFields.filter(f => f.type === 'tracking');
        const profileFields = availableFields.filter(f => f.type === 'profile');

        setSelectAllPrimary(primaryFields.length > 0 && primaryFields.every(f => selected.has(f.id)));
        setSelectAllCustom(customFields.length > 0 && customFields.every(f => selected.has(f.id)));
        setSelectAllTracking(trackingFields.length > 0 && trackingFields.every(f => selected.has(f.id)));
        setSelectAllProfile(profileFields.length > 0 && profileFields.every(f => selected.has(f.id)));
    };

    const handleExport = async () => {
        if (selectedFields.size === 0) {
            showToast('Please select at least one field to export', 'error');
            return;
        }

        setIsExporting(true);

        const selectedFieldsArray = Array.from(selectedFields);
        console.log('📤 Exporting with selected fields:', selectedFieldsArray);

        try {
            let exportOptions;

            switch (exportType) {
                case 'all':
                    exportOptions = {
                        export_data: true,
                        include_utm_data: includeUTMData,
                        export_all: true,
                        primary_filters: {},
                        custom_filters: {},
                        format: exportFormat,
                        selected_fields: selectedFieldsArray,
                        apiEndpoint: apiEndpoint
                    };
                    break;

                case 'filtered':
                    exportOptions = {
                        export_data: true,
                        include_utm_data: includeUTMData,
                        export_all: true,
                        primary_filters: currentFilters.primary_filters || {},
                        custom_filters: currentFilters.custom_filters || {},
                        format: exportFormat,
                        selected_fields: selectedFieldsArray,
                        apiEndpoint: apiEndpoint
                    };
                    break;

                case 'selected':
                    if (selectedRows.length === 0) {
                        showToast('Please select at least one lead to export', 'error');
                        return;
                    }
                    exportOptions = {
                        export_data: true,
                        include_utm_data: includeUTMData,
                        selected_lead_ids: selectedRows,
                        export_all: false,
                        format: exportFormat,
                        selected_fields: selectedFieldsArray,
                        apiEndpoint: apiEndpoint
                    };
                    break;
            }

            const url = await exportLeads(exportOptions);

            // Set the download URL and mark export as complete
            setDownloadUrl(url);
            setExportComplete(true);
            showToast('✅ Export completed! Click the download button to get your file.', 'success');

        } catch (error) {
            console.error('Export failed:', error);
            showToast(`Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
        } finally {
            setIsExporting(false);
        }
    };

    const handleDownload = () => {
        if (downloadUrl) {
            window.open(downloadUrl, '_blank');
        }
    };

    const handleClose = () => {
        // Reset state when closing
        setDownloadUrl(null);
        setExportComplete(false);
        setExportType('filtered');
        setExportFormat('csv');
        setIncludeUTMData(false);
        setStep(1);
        onClose();
    };

    const goToNextStep = () => {
        if (exportType === 'selected' && selectedRows.length === 0) {
            showToast('Please select at least one lead to export', 'error');
            return;
        }
        setStep(2);
    };

    const goToPreviousStep = () => {
        setStep(1);
    };

    const MAX_EXPORT_LIMIT = 10000;

    const getExportCount = () => {
        switch (exportType) {
            case 'all':
                return 'Up to 10,000 leads';
            case 'filtered':
                if (totalFilteredCount > MAX_EXPORT_LIMIT) {
                    return `${MAX_EXPORT_LIMIT.toLocaleString()} of ${totalFilteredCount.toLocaleString()} filtered leads`;
                }
                return `${totalFilteredCount.toLocaleString()} filtered leads`;
            case 'selected':
                return `${selectedRows.length} selected leads`;
            default:
                return '0 leads';
        }
    };

    const primaryFields = availableFields.filter(f => f.type === 'primary');
    const customFields = availableFields.filter(f => f.type === 'custom');
    const trackingFields = availableFields.filter(f => f.type === 'tracking');
    const profileFields = availableFields.filter(f => f.type === 'profile');

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
                    <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                            <Download className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Export Leads</h3>
                            <p className="text-xs text-gray-500">
                                Step {step} of 2: {step === 1 ? 'Export Options' : 'Select Fields'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6 overflow-y-auto flex-1">
                    {/* Show download button if export is complete */}
                    {exportComplete && downloadUrl && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                        <CheckCircle className="w-5 h-5 text-green-600" />
                                    </div>
                                    <div>
                                        <div className="text-sm font-semibold text-green-900">Export Complete!</div>
                                        <div className="text-xs text-green-700">Your file is ready to download</div>
                                    </div>
                                </div>
                                <button
                                    onClick={handleDownload}
                                    className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                                >
                                    <Download className="w-4 h-4" />
                                    Download
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step 1: Export Options */}
                    {!exportComplete && step === 1 && (
                        <>
                            {/* Export Type Selection */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                    What would you like to export?
                                </label>
                                <div className="space-y-3">
                                    {/* All Leads */}
                                    <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                                        <input
                                            type="radio"
                                            name="exportType"
                                            value="all"
                                            checked={exportType === 'all'}
                                            onChange={(e) => setExportType(e.target.value as any)}
                                            className="text-brandColor focus:ring-brandColor"
                                        />
                                        <div className="ml-3 flex items-center">
                                            <Database className="w-4 h-4 text-gray-500 mr-2" />
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">All Leads</div>
                                                <div className="text-xs text-gray-500">Export up to 10,000 leads from the database</div>
                                            </div>
                                        </div>
                                    </label>

                                    {/* Filtered Leads */}
                                    <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                                        <input
                                            type="radio"
                                            name="exportType"
                                            value="filtered"
                                            checked={exportType === 'filtered'}
                                            onChange={(e) => setExportType(e.target.value as any)}
                                            className="text-brandColor focus:ring-brandColor"
                                        />
                                        <div className="ml-3 flex items-center">
                                            <Filter className="w-4 h-4 text-gray-500 mr-2" />
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">Current Filter Results</div>
                                                <div className="text-xs text-gray-500">
                                                    {totalFilteredCount > MAX_EXPORT_LIMIT
                                                        ? `Export ${MAX_EXPORT_LIMIT.toLocaleString()} of ${totalFilteredCount.toLocaleString()} leads matching current filters`
                                                        : `Export ${totalFilteredCount.toLocaleString()} leads matching current filters`
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    </label>

                                    {/* Selected Leads */}
                                    <label className={`flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                                        selectedRows.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
                                    }`}>
                                        <input
                                            type="radio"
                                            name="exportType"
                                            value="selected"
                                            checked={exportType === 'selected'}
                                            onChange={(e) => setExportType(e.target.value as any)}
                                            disabled={selectedRows.length === 0}
                                            className="text-brandColor focus:ring-brandColor"
                                        />
                                        <div className="ml-3 flex items-center">
                                            <CheckCircle className="w-4 h-4 text-gray-500 mr-2" />
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">Selected Leads</div>
                                                <div className="text-xs text-gray-500">
                                                    Export {selectedRows.length} manually selected leads
                                                    {selectedRows.length === 0 && ' (No leads selected)'}
                                                </div>
                                            </div>
                                        </div>
                                    </label>
                                </div>
                            </div>

                            {/* Export Format */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                    Export Format
                                </label>
                                <div className="flex gap-3">
                                    <label className="flex-1 flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                                        <input
                                            type="radio"
                                            name="exportFormat"
                                            value="csv"
                                            checked={exportFormat === 'csv'}
                                            onChange={(e) => setExportFormat(e.target.value as 'csv' | 'excel')}
                                            className="text-brandColor focus:ring-brandColor"
                                        />
                                        <div className="ml-3">
                                            <div className="text-sm font-medium text-gray-900">CSV</div>
                                            <div className="text-xs text-gray-500">Comma-separated values</div>
                                        </div>
                                    </label>
                                    <label className="flex-1 flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                                        <input
                                            type="radio"
                                            name="exportFormat"
                                            value="excel"
                                            checked={exportFormat === 'excel'}
                                            onChange={(e) => setExportFormat(e.target.value as 'csv' | 'excel')}
                                            className="text-brandColor focus:ring-brandColor"
                                        />
                                        <div className="ml-3">
                                            <div className="text-sm font-medium text-gray-900">Excel</div>
                                            <div className="text-xs text-gray-500">.xlsx format</div>
                                        </div>
                                    </label>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Step 2: Field Selection */}
                    {!exportComplete && step === 2 && (
                        <div className="space-y-4">
                            {/* Vista Form clients: primary, custom and Vista Form fields in one section */}
                            {mergeFormFields && (
                                <div className="border border-gray-200 rounded-lg">
                                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                                        <h4 className="text-sm font-semibold text-gray-900">Form Fields</h4>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={selectAllPrimary && (customFields.length === 0 || selectAllCustom) && selectAllProfile}
                                                onChange={toggleSelectAllFormFields}
                                                className="text-brandColor focus:ring-brandColor"
                                            />
                                            <span className="text-xs text-gray-600">Select All</span>
                                        </label>
                                    </div>
                                    <div className="p-4 grid grid-cols-2 gap-2 max-h-80 overflow-y-auto">
                                        {[...primaryFields, ...customFields, ...profileFields].map(field => (
                                            <label key={field.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedFields.has(field.id)}
                                                    onChange={() => toggleField(field.id)}
                                                    className="text-brandColor focus:ring-brandColor"
                                                />
                                                <span className="text-sm text-gray-700">
                                                    {field.category && <span className="text-xs text-gray-500">[{field.category}] </span>}
                                                    {field.label}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Primary Fields */}
                            {!mergeFormFields && primaryFields.length > 0 && (
                                <div className="border border-gray-200 rounded-lg">
                                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                                        <h4 className="text-sm font-semibold text-gray-900">Primary Fields</h4>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={selectAllPrimary}
                                                onChange={() => toggleSelectAll('primary')}
                                                className="text-brandColor focus:ring-brandColor"
                                            />
                                            <span className="text-xs text-gray-600">Select All</span>
                                        </label>
                                    </div>
                                    <div className="p-4 grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                                        {primaryFields.map(field => (
                                            <label key={field.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedFields.has(field.id)}
                                                    onChange={() => toggleField(field.id)}
                                                    className="text-brandColor focus:ring-brandColor"
                                                />
                                                <span className="text-sm text-gray-700">{field.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Custom Fields */}
                            {!mergeFormFields && customFields.length > 0 && (
                                <div className="border border-gray-200 rounded-lg">
                                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                                        <h4 className="text-sm font-semibold text-gray-900">Custom Fields ({customFields.length})</h4>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={selectAllCustom}
                                                onChange={() => toggleSelectAll('custom')}
                                                className="text-brandColor focus:ring-brandColor"
                                            />
                                            <span className="text-xs text-gray-600">Select All</span>
                                        </label>
                                    </div>
                                    <div className="p-4 grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                                        {customFields.map(field => (
                                            <label key={field.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedFields.has(field.id)}
                                                    onChange={() => toggleField(field.id)}
                                                    className="text-brandColor focus:ring-brandColor"
                                                />
                                                <span className="text-sm text-gray-700">{field.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Tracking Fields */}
                            {trackingFields.length > 0 && (
                                <div className="border border-gray-200 rounded-lg">
                                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                                        <h4 className="text-sm font-semibold text-gray-900">Tracking Data (UTM, Facebook, LinkedIn)</h4>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={selectAllTracking}
                                                onChange={() => toggleSelectAll('tracking')}
                                                className="text-brandColor focus:ring-brandColor"
                                            />
                                            <span className="text-xs text-gray-600">Select All</span>
                                        </label>
                                    </div>
                                    <div className="p-4 grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                                        {trackingFields.map(field => (
                                            <label key={field.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedFields.has(field.id)}
                                                    onChange={() => toggleField(field.id)}
                                                    className="text-brandColor focus:ring-brandColor"
                                                />
                                                <span className="text-sm text-gray-700">
                                                    {field.category && <span className="text-xs text-gray-500">[{field.category}] </span>}
                                                    {field.label}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Extended Lead Profile Fields */}
                            {!mergeFormFields && profileFields.length > 0 && (
                                <div className="border border-gray-200 rounded-lg">
                                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                                        <h4 className="text-sm font-semibold text-gray-900">Vista Form</h4>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={selectAllProfile}
                                                onChange={() => toggleSelectAll('profile')}
                                                className="text-brandColor focus:ring-brandColor"
                                            />
                                            <span className="text-xs text-gray-600">Select All</span>
                                        </label>
                                    </div>
                                    <div className="p-4 grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                                        {profileFields.map(field => (
                                            <label key={field.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedFields.has(field.id)}
                                                    onChange={() => toggleField(field.id)}
                                                    className="text-brandColor focus:ring-brandColor"
                                                />
                                                <span className="text-sm text-gray-700">
                                                    {field.category && <span className="text-xs text-gray-500">[{field.category}] </span>}
                                                    {field.label}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Summary */}
                            <div className="bg-brandColor/10 border border-brandColor/30 rounded-lg p-4">
                                <div className="text-sm">
                                    <span className="font-medium text-brandColor">Selected: </span>
                                    <span className="text-brandColor">{selectedFields.size} fields</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Summary for step 1 */}
                    {!exportComplete && step === 1 && (
                        <div className="bg-brandColor/10 border border-brandColor/30 rounded-lg p-4">
                            <div className="text-sm">
                                <span className="font-medium text-brandColor">Ready to export: </span>
                                <span className="text-brandColor">{getExportCount()}</span>
                            </div>
                            <div className="text-xs text-brandColor mt-1">
                                Format: {exportFormat.toUpperCase()}
                            </div>
                            <div className="text-xs text-orange-700 mt-2 pt-2 border-t border-brandColor/30">
                                ⚠️ Maximum 10,000 leads per export
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-3 p-6 border-t border-gray-200 flex-shrink-0">
                    <div>
                        {!exportComplete && step === 2 && (
                            <button
                                onClick={goToPreviousStep}
                                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors flex items-center gap-2"
                            >
                                <ChevronLeft className="w-4 h-4" />
                                Back
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={handleClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                        >
                            {exportComplete ? 'Close' : 'Cancel'}
                        </button>
                        {!exportComplete && step === 1 && (
                            <button
                                onClick={goToNextStep}
                                className="px-6 py-2 bg-brandColor text-white text-sm font-medium rounded-lg hover:bg-brand-hover transition-colors flex items-center gap-2"
                            >
                                Next: Select Fields
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        )}
                        {!exportComplete && step === 2 && (
                            <button
                                onClick={handleExport}
                                disabled={isExporting || selectedFields.size === 0}
                                className="px-6 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                            >
                                {isExporting ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Exporting...
                                    </>
                                ) : (
                                    <>
                                        <Download className="w-4 h-4" />
                                        Export
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExportModalWithFieldSelection;
